import { useRef, useState } from 'react'
import { moderationApi } from '../../services/api'
import { Button } from '../common/Button'
import { formatTimeOffset } from './moderationFindings'
import styles from './ReviewMedia.module.css'

// ReviewMedia shows a review's images and videos, each blurred until the
// reviewer asks to see it. A video starts paused at the time it was
// flagged. Each item's content_url is a short-lived ticket Studio API
// minted for this viewer, so quarantined bytes show too. reviewId keeps
// one review's media apart from another's; onLinkExpired asks for the
// review to be read again when a link fails and no newer one is known.
export function ReviewMedia({ items = [], reviewId = '', onLinkExpired }) {
  if (!items.length) return null
  return (
    <ul className={styles.mediaList} aria-label="Post media">
      {items.map((item, index) => (
        <ReviewMediaItem key={`${reviewId}/${item.asset_id || `media-${index}`}`} item={item} onLinkExpired={onLinkExpired} />
      ))}
    </ul>
  )
}

function ReviewMediaItem({ item, onLinkExpired }) {
  const [revealed, setRevealed] = useState(false)
  const videoRef = useRef(null)
  // Every read of an open review (polled every 30 s) mints a new link once
  // the ticket's 10-minute window moves on. Handing a new src to a playing
  // video reloads it, so the item keeps the link it loaded first and takes
  // the newest one only once its own fails to load (it expired: 401). A
  // review selected again mounts afresh and starts from the newest link.
  const newest = moderationApi.reviewMediaUrl(item.content_url)
  const [src, setSrc] = useState(newest)
  const [failed, setFailed] = useState('')
  if (newest && newest !== src && (!src || failed === src)) {
    setSrc(newest)
    setFailed('')
  }
  const loadFailed = () => {
    setFailed(src)
    // Nothing newer to load: read the review again for a fresh link.
    if (newest === src) onLinkExpired?.()
  }
  const word = item.kind === 'video' ? 'video' : 'image'
  const flaggedAt = formatTimeOffset(item.flagged_at_ms)
  const jump = () => {
    const video = videoRef.current
    if (!video || item.flagged_at_ms == null) return
    video.currentTime = item.flagged_at_ms / 1000
    video.pause()
  }
  if (!src) return <li className={styles.mediaItem}><p className={styles.mediaCaption}>Preview unavailable for this {word}.</p></li>
  const mediaClass = revealed ? styles.media : `${styles.media} ${styles.concealed}`
  return (
    <li className={styles.mediaItem}>
      <div className={styles.mediaFrame}>
        {item.kind === 'video'
          ? <video ref={videoRef} src={src} className={mediaClass} controls={revealed} preload="metadata" aria-label="Video from the post" onLoadedMetadata={revealed ? jump : undefined} onError={loadFailed} />
          : <img src={src} className={mediaClass} alt="Image from the post" onError={loadFailed} />}
        {revealed ? null : (
          <button type="button" className={styles.reveal} onClick={() => { setRevealed(true); jump() }}>
            Show {word}
          </button>
        )}
      </div>
      {item.kind === 'video' && flaggedAt ? <p className={styles.mediaCaption}>Flagged at {flaggedAt}.</p> : null}
      {item.quarantined ? <p className={styles.mediaCaption}>Removed from public view while it is reviewed.</p> : null}
      {revealed && item.kind === 'video' && flaggedAt ? <Button variant="secondary" onClick={jump}>Jump to {flaggedAt}</Button> : null}
    </li>
  )
}
